// Library imports
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { shape } from 'prop-types';
// Component imports
import TwoColumnView from '../../../../components/TwoColumnView';
import TabNav from '../../../../components/TabNav';
import NoData from '../../../../components/NoData';
import Dropdown from '../../../../components/DropDown';
import ScheduleGameModal from './ScheduleGameModal';
// Util imports
import { shortName } from '../../../../utils/strings';
import { imageLoadError } from '../../../../utils';
import { getGameTeamData } from '../../utils';
// Styled components
import { CardTitle, InnerContent, TeamLogoSmall } from '../styles';
import { GameDate, GameDetailsWrapper, TeamName, ClickableGameListItem, ScheduleCard, RecordRow, RecordBadge, GameResult } from './styles';

const Schedule = ({ game, demo, sport }) => {
    const { teams } = useSelector(state => state.app);
    const [scheduleTab, setScheduleTab] = useState('Last 5 games');
    const [seasonTab, setSeasonTab] = useState('Regular Season');
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [modalShow, setModalShow] = useState(false);
    const [modalGameData, setModalGameData] = useState(null);
    const [loadingGame, setLoadingGame] = useState(false);

    const { awayTeamData: awayData, homeTeamData: homeData } = getGameTeamData(teams, game, demo, sport);

    // Schedule entries tag playoff games with `season: 'postseason'`
    const filterBySeason = schedule => {
        if (!Array.isArray(schedule)) return [];
        return schedule.filter(g => {
            const isPostseason = g.season === 'postseason' || g.postseason === true;
            if (seasonTab === 'Regular Season') return !isPostseason;
            if (seasonTab === 'Postseason') return isPostseason;
            return true;
        });
    };

    const awaySeasonSchedule = filterBySeason(awayData?.schedule);
    const homeSeasonSchedule = filterBySeason(homeData?.schedule);

    var commonOppIds = [];
    if (awaySeasonSchedule?.length && homeSeasonSchedule?.length) {
        const awaySet = new Set(awaySeasonSchedule.filter(g => g.oppId && g.points && g.opp_pts).map(item => item.oppId));
        const homeSet = new Set(homeSeasonSchedule.filter(g => g.oppId && g.points && g.opp_pts).map(item => item.oppId));
        commonOppIds = [...awaySet].filter(id => homeSet.has(id));
    }

    const getTeamDataProperty = (value, property) => teams.find(t => t[property] === value || t.city === value);

    const handleGameClick = async (scheduleGame, teamData) => {
        try {
            setLoadingGame(true);
            setModalShow(true);
            const opponentTeam = teams.find(t => t.teamId === scheduleGame.oppId);
            if (!opponentTeam) return;
            const isAway = scheduleGame.location === 'away';
            const homeTeamId = isAway ? scheduleGame.oppId : teamData.teamId;
            const awayTeamId = isAway ? teamData.teamId : scheduleGame.oppId;
            const awayTeam = teams.find(t => t.teamId === awayTeamId) || game.awayTeam;
            const homeTeam = teams.find(t => t.teamId === homeTeamId) || game.homeTeam;
            const awayScore = isAway ? Number(scheduleGame.points || 0) : Number(scheduleGame.opp_pts || 0);
            const homeScore = isAway ? Number(scheduleGame.opp_pts || 0) : Number(scheduleGame.points || 0);
            const winner = awayScore === homeScore ? null : awayScore > homeScore ? awayTeamId : homeTeamId;
            setModalGameData({
                dateTime: scheduleGame.date,
                awayTeam: { ...awayTeam, score: awayScore },
                homeTeam: { ...homeTeam, score: homeScore },
                winner,
                prediction: { awayScore, homeScore, winner, confidence: 0.5 },
            });
        } catch (error) {
            setModalGameData({ error: 'Failed to load game details' });
        } finally {
            setLoadingGame(false);
        }
    };

    const handleCloseModal = () => { setModalShow(false); setModalGameData(null); };

    useEffect(() => {
        if (commonOppIds?.length > 0) setSelectedTeam(commonOppIds[0]);
    }, commonOppIds);

    const renderGameList = (games, teamData) =>
        games.map((g, i) => {
            const resultColor = g.result === 'W' ? 'success' : g.result === 'L' ? 'danger' : 'light';
            return (
                <ClickableGameListItem key={i} color={resultColor} onClick={() => handleGameClick(g, teamData)}>
                    <GameDetailsWrapper>
                        <GameDate>{moment(g.date).format('MM/DD/YY')}</GameDate>
                        <TeamName>{g.location === 'away' ? '@' : 'vs.'} {g.oppAbbr}</TeamName>
                    </GameDetailsWrapper>
                    <GameResult color={resultColor}>
                        {g.points}-{g.opp_pts} {g.result}
                        <i className="fas fa-external-link-alt" style={{ fontSize: '0.65rem', opacity: 0.55 }} />
                    </GameResult>
                </ClickableGameListItem>
            );
        });

    const buildCard = (team, games, teamData) => {
        const showDivision = ['nfl', 'nba', 'mlb', 'nhl'].includes(sport?.toLowerCase());
        const gameTeam = team.teamId === game.awayTeam.teamId ? game.awayTeam : game.homeTeam;
        const { conference, division } = gameTeam;
        let confText = '';
        if (showDivision && conference && division) confText = `${conference} · ${division}`;
        else if (conference) confText = conference;

        return (
            <ScheduleCard>
                <CardTitle style={{ padding: '0.6rem 0.8rem 0.4rem', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                        {team.logoUrl && <TeamLogoSmall src={team.logoUrl} alt="" onError={imageLoadError} />}
                        {shortName(team)}
                    </div>
                    {confText && <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500, marginTop: 1 }}>{confText}</div>}
                </CardTitle>
                {team.recordData && (
                    <RecordRow>
                        <RecordBadge><label>W-L</label><span>{team.recordData.record || '—'}</span></RecordBadge>
                        <RecordBadge>
                            <label>{team.teamId === game.awayTeam.teamId ? 'Away' : 'Home'}</label>
                            <span>{team.teamId === game.awayTeam.teamId ? (team.recordData.awayRecord || '—') : (team.recordData.homeRecord || '—')}</span>
                        </RecordBadge>
                    </RecordRow>
                )}
                <div>
                    {!games?.length
                        ? <NoData title="No data" message="There is currently no schedule data for this team." />
                        : renderGameList(games, teamData || team)
                    }
                </div>
            </ScheduleCard>
        );
    };

    const getGames = (teamId, schedule) => schedule.filter(g => g.points && g.opp_pts).slice(-5).reverse();
    const getH2H  = (teamId, schedule) => {
        const other = teamId === game.awayTeam.teamId ? game.homeTeam.teamId : game.awayTeam.teamId;
        return schedule.filter(g => g.points && g.opp_pts && g.oppId === other).slice(0).reverse();
    };
    const getCommon = (schedule) => schedule.filter(g => g.points && g.opp_pts && selectedTeam === g.oppId).slice(0).reverse();

    const renderAway = () => {
        const schedule = awaySeasonSchedule;
        const team = awayData || game.awayTeam;
        if (scheduleTab === 'Last 5 games') return buildCard(team, getGames(team.teamId, schedule), team);
        if (scheduleTab === 'Head to Head')  return buildCard(team, getH2H(team.teamId, schedule), team);
        return buildCard(team, getCommon(schedule), team);
    };
    const renderHome = () => {
        const schedule = homeSeasonSchedule;
        const team = homeData || game.homeTeam;
        if (scheduleTab === 'Last 5 games') return buildCard(team, getGames(team.teamId, schedule), team);
        if (scheduleTab === 'Head to Head')  return buildCard(team, getH2H(team.teamId, schedule), team);
        return buildCard(team, getCommon(schedule), team);
    };

    return (
        <InnerContent fluid>
            <TabNav tabs={['Regular Season', 'Postseason']} activeTab={seasonTab} setTab={setSeasonTab} containerStyle={{ marginBottom: '0.5rem' }} />
            <TabNav
                tabs={!demo ? ['Last 5 games', 'Head to Head', 'Common Games'] : ['Last 5 games', 'Head to Head']}
                activeTab={scheduleTab}
                setTab={setScheduleTab}
                containerStyle={{ marginBottom: '0.65rem' }}
            />
            {scheduleTab === 'Common Games' && commonOppIds?.length > 0 && (
                <Dropdown
                    value={getTeamDataProperty(selectedTeam, 'teamId')?.teamName}
                    setValue={name => setSelectedTeam(Number(getTeamDataProperty(name, 'teamName')?.teamId))}
                    menuItems={commonOppIds.map(id => getTeamDataProperty(id, 'teamId')?.teamName)}
                    containerStyle={{ marginBottom: '0.75rem' }}
                />
            )}
            <TwoColumnView columnOneJSX={renderAway()} columnTwoJSX={renderHome()} />
            <ScheduleGameModal isOpen={modalShow} onClose={handleCloseModal} gameData={modalGameData} loading={loadingGame} error={modalGameData?.error} />
        </InnerContent>
    );
};

Schedule.propTypes = { game: shape({}).isRequired };
export default Schedule;
